// src/lib/shotMath.ts
//
// Shot decision engine – v1
// ---------------------------------------------
// This module turns a small set of measurable inputs
// (distance, wind, slope, temperature, humidity, energy, lie, handicap)
// into a simple, narratively-explained shot recommendation.
//
// Design goals (aligned with Math Pipeline + provisional):
// 1. Use *transparent, tunable* coefficients (no black box).
// 2. Decompose the problem into factors:
//    - wind
//    - slope (expressed in yards)
//    - environment (temperature + humidity)
//    - player energy (tired vs fresh)
//    - lie + handicap → dispersion / proximity / confidence
// 3. Produce a **“plays like” yardage** and simple confidence band
//    rather than an illusion of precision.
//
// Every helper below is a direct implementation of the “Inputs,
// Constraints and Behaviour” sections in your Math Pipeline doc.

//
// --- Types exported to the UI layer ----------------------------------------
//
//// See `src/lib/types.ts` → ShotRecommendation for the formal interface definition.
// The narrative below explains the semantic intent of those fields.

/**
 * Raw inputs from the Shot Planner UI.
 * These correspond 1:1 to the sliders / boxes on the screen.
 */
// src/lib/shotMath.ts
import type {
  Club,
  NormalizedLie,
  ConfidenceLabel,
  PlayerProfile,
  ShotInputs,
  ShotRecommendation,
} from "./types";
import { computeConfidence } from "@/lib/confidence";
import { adjustBagForAltitude } from "@/lib/altitude";
import { SlopeContext } from "@/lib/slopeContext";

// Optional convenience so UI can import types from "@/lib/shotMath" if you want.
export type { Club, PlayerProfile, ShotInputs, ShotRecommendation } from "./types";





// ...rest of your math and helper functions...


  /** Laser / rangefinder / GPS distance to target, in yards. */
 

  /** Wind speed in mph at ball height (or best estimate). */
  

  /**
   * Wind direction in golf degrees:
   *   0°   = directly downwind (helping)
   *   90°  = pure crosswind from the right
   *   180° = straight into the wind (hurting)
   */
  

  /**
   * Net slope in *yards* of effective elevation:
   * positive = plays longer (uphill / into raised tier)
   * negative = plays shorter (downhill).
   *
   * This corresponds to the “slope yards” concept in the Pipeline,
   * and replaces the older notion of “elevation feet.”
   */

  /** Ambient temperature in °F at the time of the shot. */
 

  /** Relative humidity, as 0–100%. */
 

  /**
   * Player energy level, 0–100:
   *   0   = completely exhausted
   *   100 = fully fresh
   *
  * In the text we refer to energy% as player freshness.
 * Lower energy increases dispersion and can reduce effective carry.
   */
  

  /**
   * Lie type – we keep this loose for v1 and tighten to a narrower
   * union later. At the UI we currently send strings like "fairway".
   */
  

  /** Playing handicap used to set expectations / dispersion bands. */
  


/**
 * Shot recommendation returned to the UI.
 * Names are chosen so they can be read almost as a sentence.
 */
  /** Recommended club label, e.g. "7-iron". */
 

  /**
   * “Plays like” yardage after *all* adjustments
   * (wind + slope + environment + energy).
   */


  /**
   * Estimated proximity to the hole in feet for this player and lie,
   * given the adjusted yardage. Drives the confidence message.
   */
  

  /** Categorical confidence label – e.g. "Low", "Medium", "High". */


  /**
   * Narrative explanation tying all factors together in plain English.
   * This is intentionally “patient/consumer style” rather than engineer-speak.
   */






/**
 * Narrowed lie types we actually know how to handle today.
 * We still accept generic strings in ShotInputs, but map into this set.
 */

/**
 * Simple club model.
 * This is what we’ll eventually store per-player in Firestore.
 */




//
// --- Utility functions (generic math) --------------------------------------
//

const degToRad = (deg: number) => (deg * Math.PI) / 180;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

//
// --- Club table (generic bag) ----------------------------------------------
// For now we assume a typical 10–15 handicap stock yardage map.
// These numbers match the rough bands used in the Math Pipeline.
//

const GENERIC_BAG: Club[] = [
  { name: "Pitching wedge", carryYards: 112 },
  { name: "9-iron", carryYards: 117 },
  { name: "8-iron", carryYards: 128 },
  { name: "7-iron", carryYards: 139 },
  { name: "6-iron", carryYards: 148 },
  { name: "5-iron", carryYards: 160 },
  { name: "4-iron / hybrid", carryYards: 172 },
  { name: "3-hybrid / 5-wood", carryYards: 188 },
];
function getBagForPlayer(player?: PlayerProfile): Club[] {
  if (player?.bag && player.bag.length > 0) {
    return player.bag;
  }
  return GENERIC_BAG;
}

//
// --- Wind adjustment --------------------------------------------------------
//
// Conceptual mapping to the Pipeline / provisional:
// - We decompose the wind vector into:
//
//      effectiveComponent = windSpeed * cos(theta)
//
//   where theta is the angle between ball-to-target and the wind
//   direction, in “golf degrees”.
// - We then convert mph → yards with a tunable coefficient that
//   depends on handicap (better players flight it down more).
// - Sign convention: positive yards = plays longer.
//

/**
 * Computes the *yardage* impact of wind, plus a human-readable note.
 */
  /**
   * Wind helper – corresponds to the “Wind Factor” in the Math Pipeline.
   *
   * Inputs:
   *  - rawYardage: base distance X to the target (yards)
   *  - windSpeedMph: wind speed (mph)
   *  - windDirectionDeg: 0° = downwind, 180° = straight into wind
   *  - player.handicap: used to scale how “sensitive” the player is to wind
   *
   * Model (blended with your on-course heuristics):
   *  - Base effect ~ X * (mph / 100)
   *  - Headwind uses full factor (1.0)
   *  - Tailwind uses reduced factor (0.5) to reflect over-estimated benefits
   *  - Direction scaled by cos(theta) so crosswinds have less effect
   *  - Handicap smoothly scales effect from scratch to 20-hcp (clamped −5…30)
   *
   * Returns:
   *  - yards: positive = plays longer, negative = plays shorter
   *  - note: human-readable explanation for the card
   */
function describeWind(bucket: string): string {
  switch (bucket) {
    case "helping":
      return "downwind";
    case "hurting":
      return "into wind";
    case "right-cross":
      return "crosswind from right";
    case "left-cross":
      return "crosswind from left";
    case "right-quarter-helping":
      return "quartering helping from right";
    case "left-quarter-helping":
      return "quartering helping from left";
    case "right-quarter-hurting":
      return "quartering hurting from right";
    case "left-quarter-hurting":
      return "quartering hurting from left";
    default:
      return "variable wind";
  }
}
  function computeWindAdjustmentYards(
    inputs: ShotInputs,
    player: PlayerProfile
  ): { yards: number; note: string } {
    const { rawYardage, windSpeedMph, windDirectionDeg } = inputs;

    // No distance or no wind → no adjustment.
    if (!rawYardage || windSpeedMph === 0) {
      return { yards: 0, note: "no wind adjustment" };
    }

    // Convert 0° = downwind, 180° = into the wind
    const thetaRad = degToRad(windDirectionDeg);
    const cosTheta = Math.cos(thetaRad);

    // directionalFactor sign:
    //  > 0 → headwind (plays longer)
    //  < 0 → tailwind (plays shorter)
    const directionalFactor = -cosTheta;

    // Very small component into / with the shot → treat as negligible
    if (Math.abs(directionalFactor) < 0.1 || windSpeedMph < 2) {
      return { yards: 0, note: "minimal wind effect" };
    }

    const isHeadwind = directionalFactor > 0;
    const absDir = Math.abs(directionalFactor);

    // Base “percent of distance” effect: mph/100 scaled by how directly it’s
    // into/out of the shot (abs(cos θ)).
    const baseFraction = (windSpeedMph / 100) * absDir;

    // Your heuristic: headwind full strength, tailwind half-strength
    const HEADWIND_SCALE = 1.0;
    const TAILWIND_SCALE = 0.5;

    let delta = rawYardage * baseFraction * (isHeadwind ? HEADWIND_SCALE : TAILWIND_SCALE);

    // Tailwind makes the shot play shorter → negative yards
    if (!isHeadwind) {
      delta *= -1;
    }

    // Handicap-sensitive scaling: better players see slightly *less* wind effect.
    // Clamp −5…30 so + handicaps and high caps are both respected.
    const hcp = player.handicap ?? player.handicapIndex ?? 14;
    const clampedHcp = clamp(hcp, -5, 35);

    const hNorm = clamp((clampedHcp + 5) / 25, 0, 1); // maps −5→0, 20→1

    const windFactorScratch = 1.0; // scratch / +hcp
    const windFactorAt20 = 1.3;    // ~30% more effect for 20-hcp

    const handicapFactor =
      windFactorScratch + hNorm * (windFactorAt20 - windFactorScratch);

    const yards = delta * handicapFactor;

    // Round to the nearest 0.1 yd so the narrative reads cleanly.
    const rounded = Math.round(yards * 10) / 10;

    // Direction only — the yardage itself is already shown once by the
    // "Wind X.Xy" headline in the explanation string (shotMath.ts below);
    // repeating it inside the note read as if two different numbers had
    // coincidentally matched.
    let note: string;
    if (Math.abs(rounded) < 0.5) {
      note = "minimal wind effect";
    } else if (rounded > 0) {
      note = "into wind";
    } else {
      note = "downwind";
    }

    return { yards: rounded, note };
  }

  


//
// --- Slope adjustment (in yards) -------------------------------------------
//
// Here we assume the user (or an automated layer later) has already
// converted the green book slope into “effective yards longer/shorter”.
// That is exactly the concept in the Pipeline: a slope % or degrees is
// mapped to a yardage delta; the UI slider expresses it directly as yards.
//
/**
 * Slope helper – corresponds to the “Slope / elevation → yardage” step
 * in the Math Pipeline.
 *
 * Inputs:
 *  - slopeYds: net effective slope in yards (positive = plays longer,
 *    negative = plays shorter), already converted from green-book data.
 *
 * Returns:
 *  - yards: slope adjustment in yards (added to rawYardage)
 *  - note: plain-English description (uphill vs downhill)
 */
function computeSlopeAdjustmentYards(
  inputs: ShotInputs
): { yards: number; note: string } {
  const { slopeYds } = inputs;

  if (slopeYds === 0) {
    return { yards: 0, note: "flat lie / level target" };
  }

  const rounded = Math.round(slopeYds * 10) / 10;
  const direction = slopeYds > 0 ? "uphill" : "downhill";

  return {
    yards: slopeYds,
    note: `${direction} adjustment: ${rounded.toFixed(1)} yds`,
  };
}

//
// --- Environment (temperature + humidity) ----------------------------------
//
// This is a small second-order correction, matching the Pipeline logic:
// - Baseline at ~70°F and 50–60% humidity.
// - Colder / denser air → plays slightly longer.
// - Hotter / thinner air → plays slightly shorter.
//
// We deliberately cap the effect to a few yards so it never dominates.
//
/**
 * Environment helper – implements the “Temperature / Humidity Factor”
 * from the Math Pipeline.
 *
 * Logic:
 *  - Baseline at ~70°F and 60% humidity.
 *  - Colder / denser air → slight positive yardage adjustment (plays longer).
 *  - Warmer / thinner air → slight negative adjustment (plays shorter).
 *  - Scales with rawYardage and is capped to a few yards (±3) so it
 *    never dominates the decision.
 */
function computeEnvironmentAdjustmentYards(
  inputs: ShotInputs
): { yards: number; note: string } {
  const { temperatureF, humidityPct, rawYardage } = inputs;

  const tempDeltaFrom70 = temperatureF - 70;
  const tempCoeffPerDeg = -0.05; // yards per °F at ~150 yds
  let tempYards = tempDeltaFrom70 * tempCoeffPerDeg;

  // Scale with distance – a 100-yard wedge is less sensitive than a 200-yard shot.
  const distanceScale = clamp(rawYardage / 150, 0.5, 1.4);
  tempYards *= distanceScale;

  // Humidity effect – small, mostly here for completeness.
  const humidDeltaFrom60 = humidityPct - 60;
  const humidCoeffPerPct = -0.005; // very small effect
  let humidYards = humidDeltaFrom60 * humidCoeffPerPct * distanceScale;

  const total = clamp(tempYards + humidYards, -3, 3); // never more than ±3 yds

  const rounded = Math.round(total * 10) / 10;
  let note: string;

  if (Math.abs(rounded) < 0.3) {
    note = "neutral temperature / humidity";
  } else if (rounded > 0) {
    note = `cold / dense air: +${rounded.toFixed(1)} yds`;
  } else {
    note = `warm / thin air: ${rounded.toFixed(1)} yds`;
  }

  return { yards: total, note };
}

//
// --- Energy ---------------------------------------------------------------
//
// // In the pipeline, lower energy increases dispersion and effective distance penalty.
// and slightly reducing distance. Here we flip the framing to “energy%”.
//
// Mapping:
//   energyPct (0–100) is treated as freshness
//
// We then:
//   - apply a small distance penalty at low energy
//   - increase expected dispersion / proximity at low energy
//
/**
 * * Energy adjustment
 *  - energyPct: clamped 0–100 freshness value
 *
 * Returns:
 *  - yards: extra yardage required because the player is tired
  *  - energyPct: clamped 0–100 freshness value
 *  - note: human-readable explanation for the narrative card
 */
/**
 * Energy adjustment
 *
 * Returns:
 *  - yards: extra yardage required because lower energy reduces effective distance
 *  - energyPct: clamped 0–100 freshness value
 *  - note: human-readable explanation for the narrative card
 */
function computeEnergyAdjustmentYards(
  inputs: ShotInputs
): { yards: number; energyPct: number; note: string } {
  const energyPct = clamp((inputs as any).energyPct ?? 100, 0, 100);

  // Lower energy reduces effective carry.
  // At 0% energy, assume the player effectively loses ~5% distance.
  const maxDistancePenaltyFraction = 0.05;
  const lowEnergyFraction = ((100 - energyPct) / 100) * maxDistancePenaltyFraction;

  const yardsPenalty = inputs.rawYardage * lowEnergyFraction;
  const rounded = Math.round(yardsPenalty * 10) / 10;

  let note: string;
  if (energyPct >= 90) {
    note = "fully fresh – no distance penalty";
  } else {
    note = `energy adjustment (reduced freshness): +${rounded.toFixed(1)} yds`;
  }

  return {
    yards: rounded,
    energyPct,
    note,
  };
}
//
// --- Lie / handicap → dispersion & proximity -------------------------------
//
// Here we encode a very simple proximity model, intentionally coarse,
// to set expectations rather than promise precision.
//
// Conceptual mapping to the Pipeline:
// - Start from a baseline proximity curve by handicap at ~150 yards.
// - Apply lie-based multipliers (rough / sand → larger dispersion).
// - Apply low-energy bump in dispersion.
//

/** Rough baseline proximity (feet) at ~150 yds by handicap. */
function baselineProximityFtForHandicap(handicap: number): number {
  const h = clamp(handicap, 0, 30);

  // Approximate: scratch 23–25 ft, 15-hcp ~35 ft, 30-hcp ~50 ft.
  if (h <= 5) return 25;
  if (h <= 10) return 30;
  if (h <= 20) return 38;
  return 50;
}

/** Multiplier for lie difficulty. */
function lieDispersionMultiplier(lie: NormalizedLie): number {
  switch (lie) {
    case "fairway":
      return 1.0;
    case "light-rough":
      return 1.2;
    case "heavy-rough":
      return 1.4;
    case "recovery":
      return 1.6;
    default:
      return 1.0;
  }
}


/**
 * Estimate proximity in feet given adjusted yardage, lie, handicap,
 * and energy level.
 */
/**
 * Proximity model – corresponds to the “Proximity / Confidence Bands”
 * section of the Math Pipeline.
 *
 * Steps:
 *  - baselineProximityFtForHandicap: expected proximity at ~150 yds by hcp.
 *  - lieDispersionMultiplier: lie-based widening of dispersion.
 *  - estimateProximityFt: combines adjustedYards, lie, handicap, and
 *    energyPct into an expected proximity in feet.
 */

// Normalize lie into the set we actually support in NormalizedLie.
// IMPORTANT: these literals must match src/lib/types.ts NormalizedLie exactly.
function normalizeLie(raw: string): NormalizedLie {
  const lower = (raw ?? "").toLowerCase();

  if (lower.includes("recovery")) return "recovery";
  if (lower.includes("heavy")) return "heavy-rough";
  if (lower.includes("light")) return "light-rough";
  if (lower.includes("rough")) return "light-rough";

  // You said "sand lies excluded" — treat bunker/sand as recovery for now
  // (and DO NOT return "sand" unless NormalizedLie includes it).
  if (lower.includes("sand") || lower.includes("bunker")) return "recovery";

  return "fairway";
}

/**
 * Map expected proximity into a simple categorical confidence label.
 */

/**
 * Club selection helper – implements the “Stock carry vs plays-like
 * yardage” step in the Pipeline.
 */
function pickClubForYardage(playsLikeYards: number, bag: Club[]): Club {
  const bagSortedAsc = [...bag].sort((a, b) => a.carryYards - b.carryYards);
  const chosen = bagSortedAsc.find((c) => c.carryYards >= playsLikeYards);
  return chosen ?? bagSortedAsc[bagSortedAsc.length - 1];
}

function computePlaysLikeYards(adjusted: number, raw: number): number {
  // Today this is identity-with-adjustment; later you may add temperature/altitude scaling here.
  return adjusted;
}

/**
 * Estimate proximity in feet given adjusted yardage, lie, handicap,
 * and energy level.
 */
function estimateProximityFt(
  adjustedYards: number,
  lieType: string,
  handicap: number,
  energyPct: number
): number {
  const normalized = normalizeLie(lieType ?? "fairway");

  const base = baselineProximityFtForHandicap(handicap);

  // Distance scaling: farther → more dispersion
  const distanceScale = clamp(adjustedYards / 150, 0.75, 1.5);

  const lieMult = lieDispersionMultiplier(normalized);

  // Lower energy increases dispersion up to +25% at 0% energy
  const maxLowEnergyDispersionBump = 0.25;
  const energyClamped = clamp(energyPct, 0, 100);
  const lowEnergyFactor =
    1 + ((100 - energyClamped) / 100) * maxLowEnergyDispersionBump;

  return base * distanceScale * lieMult * lowEnergyFactor;
}
//
// --- Main entry point ------------------------------------------------------
//
// This is the single function used by the UI. It wires together all
// helpers, then assembles the human-readable narrative.
//
export function computeShotRecommendation(
  inputs: ShotInputs,
  player: PlayerProfile,
  slopeContext?: SlopeContext
): ShotRecommendation {
  // PlayerProfile currently (per your types screenshot) has handicapIndex, not handicap.
  // Use handicapIndex first; if you later add `handicap`, update this line.
  const handicap = player.handicapIndex ?? 14;

  // Energy is modeled directly as a 0–100 freshness input.
  const energyPct = clamp(inputs.energyPct ?? 100, 0, 100);

  // --- adjustments (yards) ---
  const windAdj = computeWindAdjustmentYards(inputs, player);
  const slopeInputs =
    slopeContext?.approach?.slopeYds != null
      ? {
          ...inputs,
          slopeYds: slopeContext.approach.slopeYds,
        }
      : inputs;

  const slopeAdj = computeSlopeAdjustmentYards(slopeInputs);
  const envAdj = computeEnvironmentAdjustmentYards(inputs);
  const energyAdj = computeEnergyAdjustmentYards(inputs);

  const totalAdjustmentYds =
    (windAdj?.yards ?? 0) +
    (slopeAdj?.yards ?? 0) +
    (envAdj?.yards ?? 0) +
    (energyAdj?.yards ?? 0);

  const playsLikeYards = computePlaysLikeYards(
    inputs.rawYardage + totalAdjustmentYds,
    inputs.rawYardage
  );

  // --- proximity estimate ---
  const expectedProximityFt = estimateProximityFt(
    playsLikeYards,
    inputs.lieType ?? "fairway",
    handicap,
    energyPct
  );

  // --- pick club ---
  const baseBag = player.bag ?? [];
  const effectiveBag = adjustBagForAltitude(baseBag, player.altitudeFt ?? 0);

  const clubObj =
    effectiveBag.length > 0
      ? pickClubForYardage(playsLikeYards, effectiveBag)
      : { name: "PW", carryYards: 0 };

  // --- confidence ---
  const confidenceResult = computeConfidence({
    playsLikeYards,
    clubCarryYards: clubObj.carryYards,
    lieType: inputs.lieType ?? "fairway",
    slopeYds: slopeInputs.slopeYds ?? 0,
    energyPct,
    handicap,
  });

  const confidence = confidenceResult.label;

  const slopeSourceLabel =
    slopeContext?.approach?.slopeYds != null ? "elevation-derived" : "manual";

  const explanation = [
    `Plays-like: ${Math.round(playsLikeYards)}y (raw ${Math.round(
      inputs.rawYardage
    )}y, adj ${totalAdjustmentYds.toFixed(1)}y)`,
    `Wind ${(windAdj?.yards ?? 0).toFixed(1)}y (${windAdj?.note ?? "n/a"}), ` +
      `Slope ${(slopeAdj?.yards ?? 0).toFixed(1)}y (${slopeSourceLabel}), ` +
      `Env ${(envAdj?.yards ?? 0).toFixed(1)}y (${envAdj?.note ?? "n/a"}), ` +
      `Energy ${(energyAdj?.yards ?? 0).toFixed(1)}y (${energyAdj?.note ?? "n/a"})`,
    `Lie: ${inputs.lieType ?? "fairway"}, HCP: ${handicap}, Energy: ${energyPct}%, Altitude: ${Math.round(
      player.altitudeFt ?? 0
    )} ft`,
    `Expected proximity: ~${Math.round(expectedProximityFt)} ft`,
  ].join(" | ");

return {
  club: clubObj.name,
  clubCarryYards: clubObj.carryYards,
  playsLikeYards,
  expectedProximityFt,
  confidence,
  confidenceScore: confidenceResult.score,
  confidenceReasons: confidenceResult.reasons,
  explanation,
 };
}